import { gql } from '@apollo/client'

const merchantSettingPaths = `
  closed_store_message: setting(path: "closed_store_message")
  pickup_address_enabled: setting(path: "pickup_address_enabled")
`

const storeSettingsPaths = `
  delivery_radius: settings(path: "delivery_radius")
  scheduled_delivery: settings(path: "scheduled_delivery")
  scheduled_pickup: settings(path: "scheduled_pickup")
  same_day_pickup: settings(path: "same_day_pickup")
  same_day_delivery: settings(path: "same_day_delivery")
  
  table_enabled: settings(path: "table_enabled")
  table_welcome_message: settings(path: "table_welcome_message")
  table_count: settings(path: "table_count")
  table_allow_counter_pickup: settings(path: "table_allow_counter_pickup")
  table_counter_pickup_message: settings(path: "table_counter_pickup_message")
  table_choices: settings(path: "table_choices")
  
  asap_only: settings(path: "asap_only")
  asap_enabled: settings(path: "asap_enabled")

  delivery_interval: settings(path: "delivery_interval")
  pickup_interval: settings(path: "pickup_interval")

  busy_preparation_time: settings(path: "busy_preparation_time")
  moderate_preparation_time: settings(path: "moderate_preparation_time")
  quiet_preparation_time: settings(path: "quiet_preparation_time")
  prep_mode: settings(path: "prep_mode")
  
  delivery_area: settings(path: "delivery_area")
  busy_delivery_area: settings(path: "busy_delivery_area")
  busy_delivery_area_enabled: settings(path: "busy_delivery_area_enabled")
  `

export const QUERY_GET_MERCHANT = gql`
  query getMerchantBySlug($slug: String!) {
    merchants(where: { slug: { _eq: $slug } }) {
      ${merchantSettingPaths}
    }
    stores(
      where: {
        archived_at: { _is_null: true }
        merchant: { slug: { _eq: $slug } }
      }
    ) {
      id
      name
      slug
      is_open
      address {
        line_1
        line_2
        city
        zip
        geom
      }
      ${storeSettingsPaths}
      split_hours
      pre_order_settings {
        cutoffs
        delivery_enabled
        delivery_interval
        delivery_radius
        delivery_times
        pre_order_day_allowance
        pickup_times
        pickup_schedule
        pickup_interval
        pickup_enabled
        special_cutoffs_map
        delivery_area_setting
      }
    }
  }
`

export const QUERY_GET_MERCHANT_STORE = gql`
  query getMerchantAndStoreBySlug($merchant: String!, $slug: String!) {
    merchants(where: { slug: { _eq: $merchant } }) {
      closed_store_message: setting(path: "closed_store_message")
      stores(where: { slug: { _eq: $slug }, archived_at: { _is_null: true } }) {
        id
        name
        slug
        is_open
        address {
          line_1
          line_2
          city
          zip
          geom
        }
        ${storeSettingsPaths}
        split_hours
      }
    }
  }
`
const PARTNER_STORE_OFT_FRAGMENT = `
  orderFulfillmentTypes(fulfillmentDate: $fulfillmentDate, fulfillmentTime: $fulfillmentTime) {
    fulfillmentType
    minimumOrderValue
    orderType
    status
    earliestTimeslot {
      from
      to
    }
  }
`

const PARTNER_STORE_FRAGMENT = `
  id
  name
  description
  contactNumber
  slug
  status
  timezone
  dietaryRequirements

  address {
    city
    lineOne
    lineTwo
    zip
    coordinates {
      lat
      lng
    }
  }

  openingHours {
    dayOfWeek
    shifts {
      startTime
      endTime
    }
  }

  seo {
    description
  }

  ${PARTNER_STORE_OFT_FRAGMENT}
`

const PARTNER_FRAGMENT = `
  name
  status
  maxDaysInAdvance

  content {
    welcome {
      header
      message
    }
    images {
      banner {
        original
      }
      logos {
        alternative {
          original
        }
        primary {
          original
        }
      }
      splash {
        original
      }
    }
    messages {
      description
      id
      messageType
      orderType
      title
    }
    promotionalBanner
  }

  seo {
    description
    enabled
    tagline
  }

  analytics {
    enabled
    google {
      tagManager {
        id
      }
      analytics {
        id
      }
      ads {
        id
        conversionId
      }
    }
    facebook {
      pixel {
        id
      }
    }
  }

  checkoutPageSettings {
    customFieldSettings {
        applicableFulfillmentTypes
        isMandatory
        name
        placeholder
    }
    deliveryNoteSettings {
        placeholder
    }
    giftWrapSettings {
      description
      placeholder
      price
    }
    orderNoteSettings {
        placeholder
    }
  }
`

const CUSTOMER_FRAGMENT = `
  addresses {
    id
    isDefaultShippingAddress
    name
    address {
      city
      country
      flatNumber
      id
      lineOne
      lineTwo
      zip
      coordinates {
        lat
        lng
      }
    }
  }
  id
  apiKey
  phoneNumber: contactNumber
  email
  firstName
  lastName
  marketingOptIn
  salutation
`

export const QUERY_GET_PARTNER = gql`
  query Partner(
    $fulfillmentDate: Date
    $fulfillmentTime: Time
  ) {
    partner {
      ${PARTNER_FRAGMENT}
      stores {
        __typename
        ... on Store {
          ${PARTNER_STORE_FRAGMENT}
        }
      }
    }
  }
`

export const QUERY_GET_FILTERED_PARTNER = gql`
  query Partner(
    $sortDirection: SortDirection
    $rankingCriteria: StoreRankingCriteria
    $sortBy: [PartnerStoreSortBy!]
    $fulfillmentDate: Date
    $fulfillmentTime: Time
  ) {
    partner {
      ${PARTNER_FRAGMENT}
      stores(
        rankingCriteria: $rankingCriteria
        sortDirection: $sortDirection
        sortBy: $sortBy
      ) {
        __typename
        ... on Store {
          ${PARTNER_STORE_FRAGMENT}
        }
        ... on StoreBrowseResult {
          distanceFromPoint
          rankReasons
          store {
            ${PARTNER_STORE_FRAGMENT}
          }
        }
      }
    }
  }
`

export const QUERY_GET_PARTNER_AND_CUSTOMER = gql`
  query PartnerAndCustomer(
    $fulfillmentDate: Date
    $fulfillmentTime: Time
  ) {
    partner {
      ${PARTNER_FRAGMENT}
      stores {
        __typename
        ... on Store {
          ${PARTNER_STORE_FRAGMENT}
        }
      }
    }
    customer {
      ${CUSTOMER_FRAGMENT}
    }
  }
`
